import React, { useState } from 'react';
import {
  Card,
  Input,
  Button,
  Table,
  Typography,
  Space,
  Alert,
  Tag,
  Empty,
  Divider,
  Switch,
  message,
  notification,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { searchIPs } from '../api/client';
import { accountLabel } from '../utils/accountNames';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// ─── Match result table columns ───────────────────────────────────────────────
//
// This table is shown once per searched IP address, listing every SG rule
// that matched that IP.
//
const MATCH_COLUMNS = [
  {
    title: 'Security Group',
    key: 'sg',
    render: (_, record) => (
      <Space direction="vertical" size={0}>
        <Link to={`/security-groups?sg=${record.sg_id}`}>
          <Text strong style={{ color: 'inherit' }}>{record.sg_name}</Text>
        </Link>
        <Text type="secondary" style={{ fontSize: 12 }}>{record.sg_id}</Text>
      </Space>
    ),
  },
  {
    title: 'Matched CIDR',
    dataIndex: 'cidr_ip',
    key: 'cidr_ip',
    render: (cidr) => <Text code>{cidr}</Text>,
  },
  {
    title: 'Protocol',
    dataIndex: 'protocol',
    key: 'protocol',
    render: (proto) => (
      <Tag color={proto === '-1' ? 'red' : 'blue'}>
        {proto === '-1' ? 'All Traffic' : (proto ?? '').toUpperCase()}
      </Tag>
    ),
  },
  {
    title: 'Ports',
    key: 'ports',
    render: (_, record) => {
      if (record.protocol === '-1') {
        return <Text type="secondary">All</Text>;
      }
      if (record.from_port === record.to_port) {
        return <Text>{record.from_port}</Text>;
      }
      return <Text>{record.from_port} – {record.to_port}</Text>;
    },
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    render: (d) => d || <Text type="secondary">—</Text>,
  },
  {
    title: 'Account',
    dataIndex: 'account_id',
    key: 'account_id',
    render: (id) => <Text code style={{ fontSize: 12 }}>{accountLabel(id)}</Text>,
  },
  {
    title: 'Region',
    dataIndex: 'region',
    key: 'region',
  },
];

// ─── IPSearch Component ───────────────────────────────────────────────────────

const IPSearch = () => {
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOpenToWorld, setShowOpenToWorld] = useState(false);

  // null  = search not yet performed (show empty state)
  // {}    = search performed, results object keyed by IP
  const [results, setResults] = useState(null);

  const OPEN_TO_WORLD = ['0.0.0.0/0', '::/0'];

  // ── Parse the text area and run the search ─────────────────────────────────

  const handleSearch = async () => {
    // Split on newlines, trim whitespace, remove blank lines
    const ips = inputText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (ips.length === 0) {
      message.warning('Enter at least one IP address before searching.');
      return;
    }

    setLoading(true);
    try {
      const data = await searchIPs(ips);
      // Lambda returns results as an array: [{ ip, normalised, matches }]
      // Convert to object keyed by IP for easy per-IP rendering.
      const resultArray = data.results ?? [];
      const resultMap = {};
      resultArray.forEach((entry) => {
        resultMap[entry.ip] = {
          found: (entry.matches ?? []).length > 0,
          matches: entry.matches ?? [],
          normalised: entry.normalised,
          error: entry.error,
        };
      });
      setResults(resultMap);
    } catch (err) {
      notification.error({
        message: 'Search failed',
        description: err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Check your API connection and try again.',
        duration: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Allow Ctrl+Enter (or Cmd+Enter on Mac) as a keyboard shortcut to search
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSearch();
    }
  };

  const searchedIPs = results ? Object.keys(results) : [];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>

      {/* ── Page Header ────────────────────────────────────────────────── */}
      <Title level={4} style={{ margin: 0 }}>
        IP Address Search
      </Title>

      {/* ── Search Input Card ──────────────────────────────────────────── */}
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong style={{ fontSize: 15 }}>
              Enter IP addresses to search
            </Text>
            <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 13 }}>
              One IP address or CIDR block per line. Searches all ~1,000 Security Group
              rules across every account and region. Press{' '}
              <Text keyboard>Ctrl</Text> + <Text keyboard>Enter</Text> to search.
            </Paragraph>
          </div>

          <TextArea
            rows={6}
            placeholder={`203.0.113.10\n198.51.100.0/24\n192.0.2.5`}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ fontFamily: 'monospace', fontSize: 13 }}
          />

          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={loading}
            onClick={handleSearch}
            disabled={inputText.trim().length === 0}
            size="large"
          >
            Search Security Group Rules
          </Button>
        </Space>
      </Card>

      {/* ── Empty state — shown before any search ──────────────────────── */}
      {results === null && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text type="secondary">
              Enter one or more IP addresses above and click Search.
            </Text>
          }
          style={{ padding: '32px 0' }}
        />
      )}

      {/* ── Results — one Card per searched IP ─────────────────────────── */}
      {/* ── Open-to-world toggle — only shown once there are results ──────── */}
      {searchedIPs.length > 0 && (
        <Space>
          <Switch
            checked={showOpenToWorld}
            onChange={setShowOpenToWorld}
            size="small"
          />
          <Text type="secondary">
            Show open-to-world rules (0.0.0.0/0)
          </Text>
        </Space>
      )}

      {searchedIPs.map((ip) => {
        const entry = results[ip];
        const allMatches = entry?.matches ?? [];
        const matches = showOpenToWorld
          ? allMatches
          : allMatches.filter((m) => !OPEN_TO_WORLD.includes(m.cidr_ip));
        const hiddenCount = allMatches.length - matches.length;
        const found = matches.length > 0;

        return (
          <Card key={ip}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">

              {/* IP address heading */}
              <Text strong style={{ fontSize: 17, fontFamily: 'monospace' }}>
                {ip}
              </Text>

              {/* Found / not found alert */}
              {found ? (
                <Alert
                  type="success"
                  showIcon
                  message={`Found in ${matches.length} rule${matches.length !== 1 ? 's' : ''}${hiddenCount > 0 ? ` (+${hiddenCount} open-to-world hidden)` : ''}`}
                  description={`This IP address is allowlisted in ${matches.length} specific rule${matches.length !== 1 ? 's' : ''}.${hiddenCount > 0 ? ` Toggle "Show open-to-world rules" to see ${hiddenCount} additional 0.0.0.0/0 match${hiddenCount !== 1 ? 'es' : ''}.` : ''}`}
                />
              ) : (
                <Alert
                  type={hiddenCount > 0 ? 'warning' : 'error'}
                  showIcon
                  message={hiddenCount > 0 ? `No specific rules — but ${hiddenCount} open-to-world rule${hiddenCount !== 1 ? 's' : ''} exist` : 'Not found in any Security Group'}
                  description={hiddenCount > 0 ? `No rules specifically allow this IP. Toggle "Show open-to-world rules" to see the ${hiddenCount} 0.0.0.0/0 match${hiddenCount !== 1 ? 'es' : ''}.` : 'This IP address is not currently allowlisted in any rule across all monitored accounts and regions.'}
                />
              )}

              {/* Matches table — only shown when there are results */}
              {found && matches.length > 0 && (
                <>
                  <Divider style={{ margin: '4px 0' }} />
                  <Table
                    dataSource={matches}
                    columns={MATCH_COLUMNS}
                    rowKey={(record, idx) =>
                      record.rule_id ?? `${record.sg_id}-${idx}`
                    }
                    pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} rules` }}
                    size="small"
                    scroll={{ x: 700 }}
                  />
                </>
              )}

            </Space>
          </Card>
        );
      })}

    </Space>
  );
};

export default IPSearch;
